/**
 * Page 6 — Touch profile. Everything here cross-filters.
 *
 * The month chart always shows ALL months and the department bars always show ALL
 * departments, so you can switch selection without clearing first. The selection bar
 * names what is held, each with its own ×.
 */
import { useMemo } from 'react'
import { useAppData } from '@/data/AppData'

import { DataTable, type Column } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import { CardHeading, PopOut } from '@/components/PopOut'
import { StatStrip, Stat, PersonCell } from '@/components/Bits'
import { RankBars, type RankBar } from '@/components/Charts'
import { StackColumns } from '@/components/StackColumns'
import { int, pct, dec } from '@/format'
import { shoreVendor } from '@/data/selectors'
import { useUrlState, maybeText } from '@/ui/urlState'
import { useOpenTouchDetail } from '@/data/touchDrill'
import { useAgeLimits } from '@/data/payerTypeSettings'

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

/**
 * The rule-of-record touch buckets (MEASURE-RULES.md §5.1–5.2), added to the bundle
 * alongside the older FactClaimTouches-based fields. Every resolved allocation lands
 * in exactly one bucket, so the three sum to this page's resolved count: the claims
 * resolved in the month, billed any month — `resolvedClaims`'s basis since review #397
 * (by bill month before, which tied to nothing else).
 *
 * A fourth since review #695 (MEASURE-RULES Rule 5.7, as Shayna rewrote it 2026-10-18):
 * no touch, but resolved more than its payer type's age limit after it was sent, likely
 * worked outside the FCC tool. It is left out of every share's base (`base`): the 0%
 * touch share, touched, one and two-or-more are over the claims within the limits. It
 * is shown on its own, as a count and a share of ALL the resolved claims (`resolved`).
 */
type RuleTouch = {
  resolvedNoTouch?: number
  resolvedNoTouchAged?: number
  resolvedOneTouch?: number
  resolvedTwoPlusTouch?: number
  resolvedTouches?: number
}

function buckets(rows: readonly RuleTouch[]) {
  let no = 0
  let aged = 0
  let one = 0
  let two = 0
  let touches = 0
  for (const r of rows) {
    no += r.resolvedNoTouch ?? 0
    aged += r.resolvedNoTouchAged ?? 0
    one += r.resolvedOneTouch ?? 0
    two += r.resolvedTwoPlusTouch ?? 0
    touches += r.resolvedTouches ?? 0
  }
  return { no, aged, one, two, touches, touched: one + two, base: no + one + two, resolved: no + aged + one + two }
}

/** What the over-the-limit bucket is called. */
const AGED_LABEL = 'Over the age limit'

export function TouchProfile() {
  const { scope, openMonths, openPerson } = useAppData()
  // In the URL, so Back from a drill returns to the same month and department.
  const [heldMonth, setMonth] = useUrlState('month', null, maybeText)
  // A held month the top bar's range has since left out is not held any more; keeping
  // it would empty the page with nothing on it saying why.
  const month = heldMonth !== null && scope.months.includes(heldMonth) ? heldMonth : null
  const [dept, setDept] = useUrlState('dept', null, maybeText)
  const openTouches = useOpenTouchDetail()
  const ageLimits = useAgeLimits()
  const anyLimit = ageLimits.limits.size > 0
  /** The limits in words: "Medicare 90 days, HMO 60 days". */
  const limitsText = [...ageLimits.limits].map(([t, d]) => `${t} ${d} days`).join(', ')
  const limitNote =
    ageLimits.status === 'unavailable'
      ? 'Payer-type age limits could not be read: no limit applied.'
      : !anyLimit
        ? 'No payer type has an age limit set (Rules page).'
        : `No touch, resolved past the payer type’s limit (${limitsText}). Left out of every share here.`

  /** Rows surviving the page's own selection, on top of the global filters. */
  const selected = useMemo(
    () => scope.people.filter((p) => (dept === null || p.department === dept)),
    [scope.people, dept],
  )

  /** Touch rows for the held month, or every month. */
  const touchRows = useMemo(
    () =>
      selected.flatMap((p) =>
        p.months
          .filter((m) => month === null || m.month === month)
          .map((m) => ({ person: p, row: m })),
      ),
    [selected, month],
  )

  const s = useMemo(() => {
    const b = buckets(touchRows.map((t) => t.row as RuleTouch))
    const share = (n: number) => (b.base === 0 ? null : n / b.base)
    return {
      resolved: b.resolved,
      base: b.base,
      noTouch: b.no,
      agedCount: b.aged,
      // Of every resolved claim: the bucket is outside the other shares' base.
      aged: b.resolved === 0 ? null : b.aged / b.resolved,
      oneCount: b.one,
      twoCount: b.two,
      touched: b.touched,
      zero: share(b.no),
      touchedShare: share(b.touched),
      one: share(b.one),
      two: share(b.two),
      touchesPerTouched: b.touched === 0 ? null : b.touches / b.touched,
    }
  }, [touchRows])

  /** Month chart always shows every month, whatever is held. */
  const byMonth = useMemo(() => {
    const map = new Map<string, RuleTouch[]>()
    for (const p of selected)
      for (const m of p.months) {
        const list = map.get(m.month)
        if (list) list.push(m as RuleTouch)
        else map.set(m.month, [m as RuleTouch])
      }
    return [...map.entries()]
      .map(([m, rows]) => [m, buckets(rows)] as const)
      .sort(([a], [b]) => (a < b ? -1 : 1))
  }, [selected])

  /** Department bars always show every department. */
  const byDept = useMemo(() => {
    const map = new Map<string, { no: number; aged: number; base: number; resolved: number; touched: number; people: number }>()
    for (const p of scope.people) {
      const rows = p.months.filter((m) => month === null || m.month === month)
      if (!rows.length) continue
      const b = buckets(rows as RuleTouch[])
      const e = map.get(p.department) ?? { no: 0, aged: 0, base: 0, resolved: 0, touched: 0, people: 0 }
      e.no += b.no
      e.aged += b.aged
      e.base += b.base
      e.resolved += b.resolved
      e.touched += b.touched
      if (b.resolved > 0) e.people += 1
      map.set(p.department, e)
    }
    /*
     * A department that resolved nothing has no touch share — not a touch share of
     * zero. Drawn as a bar it was a full-width empty track with "—" beside it, which
     * reads as "0% of claims touched", the exact opposite of "no claims to touch".
     * MCS, Opex and Hospice are all in that state, so three of nine rows were saying
     * something false. They are left out, as the Ledger does.
     */
    const all = [...map.entries()].map(([d, v]) => ({ dept: d, ...v }))
    return {
      bars: all
        .filter((e) => e.base > 0)
        .map((e): RankBar => {
          const share = e.no / e.base
          return {
            key: e.dept,
            label: e.dept,
            value: share,
            right: pct(share),
            // The Ledger's bands: mostly untouched is good, under 70% needs a look.
            color: share >= 0.9 ? 'var(--good)' : share >= 0.7 ? 'var(--s1)' : 'var(--crit)',
            tip: [
              ['0% touch share', pct(share)],
              ['Resolved claims', int(e.resolved)],
              ...(e.aged > 0 ? ([[AGED_LABEL, `${int(e.aged)}, left out`]] as [string, string][]) : []),
              ['Touched', int(e.touched)],
              ['People', int(e.people)],
            ],
          }
        })
        .sort((a, b) => b.value - a.value),
    }
  }, [scope.people, month])

  const personRows = useMemo(() => {
    return selected
      .map((p) => {
        const rows = p.months.filter((m) => month === null || m.month === month)
        const b = buckets(rows as RuleTouch[])
        const share = (n: number) => (b.base === 0 ? null : n / b.base)
        return {
          person: p,
          resolved: b.resolved,
          noCount: b.no,
          agedCount: b.aged,
          // Of every resolved claim: outside the other shares' base.
          aged: b.resolved === 0 ? null : b.aged / b.resolved,
          touched: b.touched,
          touches: b.touches,
          zero: share(b.no),
          one: share(b.one),
          two: share(b.two),
          touchesPerTouched: b.touched === 0 ? null : b.touches / b.touched,
        }
      })
      .filter((r) => r.resolved > 0)
  }, [selected, month])

  type Row = (typeof personRows)[number]

  /** Her eight columns, in her order and with her headings, and Touches (#47). */
  const columns: Column<Row>[] = [
    {
      key: 'person',
      header: 'Person',
      sort: (r) => r.person.employee,
      render: (r) => (
        <PersonCell name={r.person.employee} meta={`${r.person.jobTitle}${shoreVendor(r.person)}`} />
      ),
    },
    /*
     * Review #47 (2026-10-02): "Add here touches and if i click on it, it takes me to
     * the corresponding touches." The touches on their resolved claims in the months
     * on screen, which Touch detail's claim basis lists row for row (MEASURE-RULES
     * Rule 5.6) — the held month travels with the drill.
     */
    {
      key: 'touches',
      header: 'Touches',
      align: 'right',
      sort: (r) => r.touches,
      render: (r) =>
        r.touches === 0 ? (
          <span className="mono" style={{ fontSize: 'var(--t-table)' }}>0</span>
        ) : (
          <button
            type="button"
            className="mono"
            data-testid="touches-drill"
            title="Open these touches in Touch detail"
            onClick={(e) => {
              e.stopPropagation()
              openTouches({
                employees: [r.person.employee],
                label: r.person.employee,
                basis: 'claim',
                ...(month !== null ? { months: [month] } : {}),
              })
            }}
            style={{
              fontSize: 'var(--t-table)',
              border: 0,
              background: 'none',
              padding: 0,
              color: 'var(--accent)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {int(r.touches)}
          </button>
        ),
    },
    {
      key: 'resolved',
      header: 'Resolved',
      align: 'right',
      sort: (r) => r.resolved,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{int(r.resolved)}</span>,
    },
    {
      key: 'zero',
      header: '0% touch',
      align: 'right',
      sort: (r) => r.zero,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{pct(r.zero)}</span>,
    },
    {
      key: 'noCount',
      header: '0% touch claims',
      align: 'right',
      sort: (r) => r.noCount,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{int(r.noCount)}</span>,
    },
    {
      key: 'aged',
      header: 'Over age limit',
      align: 'right',
      sort: (r) => r.aged,
      render: (r) => (
        <span
          className="mono"
          style={{ fontSize: 'var(--t-table)' }}
          title="Of all their resolved claims: no touch, resolved past the payer type’s age limit. Left out of the 0% touch share."
        >
          {anyLimit ? pct(r.aged) : '—'}
        </span>
      ),
    },
    {
      key: 'agedCount',
      header: 'Over age limit claims',
      align: 'right',
      sort: (r) => r.agedCount,
      render: (r) => (
        <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
          {anyLimit ? int(r.agedCount) : '—'}
        </span>
      ),
    },
    {
      key: 'one',
      header: 'One touch',
      align: 'right',
      sort: (r) => r.one,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{pct(r.one)}</span>,
    },
    {
      key: 'two',
      header: 'Two or more',
      align: 'right',
      sort: (r) => r.two,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{pct(r.two)}</span>,
    },
    {
      key: 'touched',
      header: 'Touched claims',
      align: 'right',
      sort: (r) => r.touched,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{int(r.touched)}</span>,
    },
    {
      key: 'tpt',
      header: 'Touches/touched',
      align: 'right',
      sort: (r) => r.touchesPerTouched,
      render: (r) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{dec(r.touchesPerTouched)}</span>,
    },
  ]

  const held = month !== null || dept !== null

  return (
    <>
      <h1 className="page-title">0% touch against touched</h1>

      <div style={{ marginTop: 16 }}>
        <StatStrip cols={6}>
          <Stat
            label="0% touch share"
            value={pct(s.zero)}
            note={`${int(s.noTouch)} of ${int(s.base)} resolved claims${s.agedCount ? ' within the age limits' : ''}`}
            hero
          />
          <Stat
            label={AGED_LABEL}
            value={anyLimit ? pct(s.aged) : '—'}
            note={anyLimit ? `${int(s.agedCount)} of ${int(s.resolved)} resolved claims. ${limitNote}` : limitNote}
          />
          <Stat
            label="Touched share"
            value={pct(s.touchedShare)}
            note={`${int(s.touched)} claims needed work`}
          />
          <Stat label="One touch" value={pct(s.one)} note={`${int(s.oneCount)} claims`} />
          <Stat label="Two or more" value={pct(s.two)} note={`${int(s.twoCount)} claims`} />
          <Stat
            label="Touches per touched claim"
            value={dec(s.touchesPerTouched)}
            note="avg work per worked claim"
          />
        </StatStrip>
      </div>

      {held && (
        <div className="selbar" style={{ margin: '12px 0 0' }}>
          <div>
            <b>Holding this tab to</b>{' '}
            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
              {int(s.resolved)} resolved claims across {int(personRows.length)} people
            </span>
          </div>
          <div className="selbar-acts">
            {dept !== null && (
              <button type="button" onClick={() => setDept(null)}>
                {dept} ×
              </button>
            )}
            {month !== null && (
              <button type="button" onClick={() => setMonth(null)}>
                {MONTH_LABEL(month)} ×
              </button>
            )}
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setMonth(null)
                setDept(null)
              }}
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      <section className="section">
        {/* One height, edges level (review #109, 2026-10-06: "Lets align these charts"). */}
        <div
          className="even-pair"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 16 }}
        >
          <PopOut title="Touch mix by month">
            {({ button }) => (
              <div className="card">
                <CardHeading button={button}>Touch mix by month</CardHeading>
                <div style={{ marginTop: 12 }}>
                  <StackColumns
                    categories={byMonth.map(([m]) => ({ key: m, label: MONTH_LABEL(m) }))}
                    series={[
                      { key: '0', label: '0% touch', color: 'var(--ord1)', fg: 'var(--ord1-fg)', values: byMonth.map(([, v]) => v.no) },
                      ...(anyLimit
                        ? [{ key: 'aged', label: AGED_LABEL, color: 'var(--s6)', fg: 'var(--ord1-fg)', values: byMonth.map(([, v]) => v.aged) }]
                        : []),
                      { key: '1', label: 'One touch', color: 'var(--ord2)', fg: 'var(--ord2-fg)', values: byMonth.map(([, v]) => v.one) },
                      { key: '2', label: 'Two or more', color: 'var(--ord3)', fg: 'var(--ord3-fg)', values: byMonth.map(([, v]) => v.two) },
                    ]}
                    selected={month}
                    onSelect={(m) => setMonth(month === m ? null : m)}
                    partial={openMonths}
                  />
                </div>
              </div>
            )}
          </PopOut>

          <PopOut title="0% touch share by department">
            {({ button }) => (
              <div className="card">
                <CardHeading button={button}>0% touch share by department</CardHeading>
                <div style={{ marginTop: 12 }}>
                  <RankBars
                    items={byDept.bars}
                    selected={dept}
                    onSelect={(k) => setDept(dept === k ? null : k)}
                    labelWidth={150}
                    formatAxis={(v) => `${Math.round(v * 100)}%`}
                    clickHint="Click to narrow the tab"
                  />
                </div>
              </div>
            )}
          </PopOut>
        </div>
      </section>

      <section className="section">
        <PopOut title="Touch profile by person">
          {({ button, enlarged }) => (
            <>
              {!enlarged && <h2 className="section-heading">Touch profile by person</h2>}
              <ExportBar
                lead={button}
                filename="touch-profile-by-person.csv"
                rows={personRows}
                columns={[
                  { header: 'Person', value: (r) => r.person.employee },
                  { header: 'Job title', value: (r) => r.person.jobTitle },
                  { header: 'Touches', value: (r) => r.touches },
                  { header: 'Resolved', value: (r) => r.resolved },
                  { header: '0% touch', value: (r) => r.zero },
                  { header: '0% touch claims', value: (r) => r.noCount },
                  { header: 'Over the age limit %', value: (r) => r.aged },
                  { header: 'Over the age limit claims', value: (r) => r.agedCount },
                  { header: 'One touch', value: (r) => r.one },
                  { header: 'Two or more', value: (r) => r.two },
                  { header: 'Touched claims', value: (r) => r.touched },
                  { header: 'Touches/touched', value: (r) => r.touchesPerTouched },
                ]}
              />
              <div className="card popout-fill" style={{ padding: 0, overflow: 'hidden' }}>
                <DataTable
                  storageKey="touch-profile.rows"
                  sortParam="sort"
                  rows={personRows}
                  columns={columns}
                  rowKey={(r) => r.person.employee}
                  onRowClick={(r) => openPerson(r.person.employee)}
                  initialSort="resolved"
                  initialDir="desc"
                  maxHeight={560}
                />
              </div>
            </>
          )}
        </PopOut>
      </section>
    </>
  )
}
